import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Building2, Briefcase, DoorClosed, ClipboardList, HardHat } from "lucide-react";
import { api } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n";
import { formatAddress } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ICONS = {
  companies: Briefcase,
  properties: Building2,
  units: DoorClosed,
  work_orders: ClipboardList,
  subs: HardHat,
};

export const GlobalSearch = () => {
  const { t } = useT();
  const { brandParam } = useBrand();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!open) {
      setQ("");
      setGroups([]);
      setActive(0);
    }
  }, [open]);

  useEffect(() => {
    if (q.trim().length < 2) {
      setGroups([]);
      return;
    }
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search", { params: { q, brand: brandParam } });
        setGroups(data.groups || []);
        setActive(0);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(id);
  }, [q, brandParam]);

  const flat = groups.flatMap((g) => g.items.map((item) => ({ ...item, __type: g.type })));

  const go = useCallback(
    (item) => {
      setOpen(false);
      if (item.__type === "companies") navigate(`/companias/${item.id}`);
      else if (item.__type === "properties") navigate(`/edificios/${item.id}`);
      else if (item.__type === "units") navigate(`/edificios/${item.property_id}?tab=units`);
      else if (item.__type === "work_orders") navigate(`/ordenes/${item.id}`);
      else navigate("/ordenes");
    },
    [navigate]
  );

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && flat[active]) {
      e.preventDefault();
      go(flat[active]);
    }
  };

  const label = (item) => {
    if (item.__type === "properties") return item.nombre;
    if (item.__type === "units") return item.etiqueta;
    if (item.__type === "work_orders") return `${item.code} · ${item.titulo}`;
    return item.nombre;
  };

  const sub = (item) => {
    if (item.__type === "properties") return formatAddress(item.direccion);
    if (item.__type === "units") return item.property_nombre;
    if (item.__type === "companies") return item.tipo;
    if (item.__type === "subs") return item.empresa;
    return item.status;
  };

  let cursor = -1;

  return (
    <>
      <button
        data-testid="global-search-trigger"
        onClick={() => setOpen(true)}
        className="flex h-8 w-[360px] max-w-[46vw] items-center gap-2 rounded-control border border-line bg-paper px-2.5 text-left text-[13px] text-ink-secondary transition-colors duration-[120ms] hover:border-line-strong"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="flex-1 truncate">{t("search.placeholder")}</span>
        <kbd className="rounded-[2px] border border-line px-1.5 py-0.5 font-mono text-[10.5px] text-ink-tertiary">
          {t("search.hint")}
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="global-search-dialog"
          className="top-[15%] max-w-[640px] translate-y-0 gap-0 overflow-hidden rounded-surface p-0"
        >
          <DialogTitle className="sr-only">{t("search.placeholder")}</DialogTitle>
          <DialogDescription className="sr-only">{t("search.placeholder")}</DialogDescription>
          <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
            <Search className="h-4 w-4 text-ink-tertiary" />
            <input
              ref={inputRef}
              autoFocus
              data-testid="global-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t("search.placeholder")}
              className="w-full bg-transparent text-[14px] text-ink outline-none placeholder:text-ink-tertiary"
            />
          </div>
          <div className="max-h-[420px] overflow-y-auto py-2" data-testid="global-search-results">
            {q.trim().length < 2 && (
              <p className="px-4 py-6 text-[13px] text-ink-secondary">{t("search.typing")}</p>
            )}
            {q.trim().length >= 2 && !loading && !flat.length && (
              <p className="px-4 py-6 text-[13px] text-ink-secondary" data-testid="global-search-empty">
                {t("search.empty")}
              </p>
            )}
            {groups.map((group) => {
              const Icon = ICONS[group.type] || Search;
              return (
                <div key={group.type} className="mb-1">
                  <p className="px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.08em] text-ink-tertiary">
                    {t(`search.groups.${group.type}`)}
                  </p>
                  {group.items.map((item) => {
                    cursor += 1;
                    const isActive = cursor === active;
                    const enriched = { ...item, __type: group.type };
                    return (
                      <button
                        key={`${group.type}-${item.id}`}
                        data-testid={`search-result-${item.id}`}
                        onMouseEnter={() => setActive(flat.findIndex((f) => f.id === item.id))}
                        onClick={() => go(enriched)}
                        className={cn(
                          "flex w-full items-center gap-3 border-l-2 px-4 py-2 text-left transition-colors duration-[120ms]",
                          isActive ? "border-brand bg-surface-sunken" : "border-transparent hover:bg-surface-sunken"
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0 text-ink-tertiary" strokeWidth={1.5} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13.5px] font-medium text-ink">
                            {label(enriched)}
                          </span>
                          <span className="block truncate text-[12px] text-ink-secondary">
                            {sub(enriched)}
                          </span>
                        </span>
                        {item.brand && (
                          <span className="rounded-[2px] border border-line px-1.5 py-0.5 font-mono text-[10.5px] uppercase text-ink-tertiary">
                            {item.brand === "bb" ? "B&B" : "Betos"}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
